import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartColumn, faGear } from "@fortawesome/free-solid-svg-icons";
import PerformanceGauge from "../../graphs/PerformanceGauge";
import Tooltip from "../Tooltip";

const LOOKBACK_MONTHS = 24;
const WEEKDAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(monthDate, count) {
  return new Date(monthDate.getFullYear(), monthDate.getMonth() + count, 1);
}

function monthIndex(date) {
  return date.getFullYear() * 12 + date.getMonth();
}

function formatMonthYear(date) {
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function ordinal(day) {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${day}th`;
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
}

function formatSelectedDay(date) {
  const month = date.toLocaleDateString("en-US", { month: "long" });
  return `${month} ${ordinal(date.getDate())}, ${date.getFullYear()}`;
}

function formatTooltipDay(date) {
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  return `${weekday}, ${formatSelectedDay(date)}`;
}

function dayCellLabel(date, viewMonth) {
  if (date.getMonth() === viewMonth) return String(date.getDate());
  return `${date.getMonth() + 1} / ${date.getDate()}`;
}

function buildCalendarDays(viewMonthDate) {
  const year = viewMonthDate.getFullYear();
  const month = viewMonthDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const gridStart = new Date(year, month, 1 - firstOfMonth.getDay());
  const lastOfMonth = new Date(year, month + 1, 0);
  const days = [];
  const cursor = new Date(gridStart);

  while (cursor <= lastOfMonth || cursor.getDay() !== 0) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

function sameDay(a, b) {
  return Boolean(a && b)
    && a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

const PREVIOUS_MONTH_OPACITY = 0.5;
const FUTURE_DAY_OPACITY = 0.1;

function dateKey(date) {
  return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

function unit(seed) {
  let x = seed | 0;
  x = Math.imul(x ^ (x >>> 16), 0x7feb352d);
  x = Math.imul(x ^ (x >>> 15), 0x846ca68b);
  x = (x ^ (x >>> 16)) >>> 0;
  return x / 4294967296;
}

function percentFromSeed(seed) {
  const biased = Math.pow(unit(seed), 0.55);
  return Math.round(22 + biased * 78);
}

function maskMissingLayers(values, seed) {
  const keys = ["grid", "solar", "storage"];
  const dropCount = unit(seed) > 0.5 ? 2 : 1;
  const start = Math.floor(unit(seed + 11) * keys.length);
  const next = { ...values };
  for (let i = 0; i < dropCount; i += 1) {
    next[keys[(start + i) % keys.length]] = null;
  }
  return next;
}

function specialsForMonth(year, month, today) {
  const last = new Date(year, month + 1, 0).getDate();
  const passed = [];
  for (let day = 1; day <= last; day += 1) {
    if (new Date(year, month, day) < today) passed.push(day);
  }
  const ranked = [...passed].sort((a, b) => {
    const left = unit(year * 10000 + (month + 1) * 100 + a + 777);
    const right = unit(year * 10000 + (month + 1) * 100 + b + 777);
    return left - right || a - b;
  });
  return {
    noData: ranked[0] ?? null,
    errors: ranked.slice(1, 3),
  };
}

function performanceForDate(date, today, cache) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const cacheKey = `${year}-${month}`;
  if (!cache.has(cacheKey)) cache.set(cacheKey, specialsForMonth(year, month, today));
  const specials = cache.get(cacheKey);
  const seed = dateKey(date);
  if (date.getDate() === specials.noData) return { grid: null, solar: null, storage: null };
  const values = {
    grid: percentFromSeed(seed + 1),
    solar: percentFromSeed(seed + 2),
    storage: percentFromSeed(seed + 3),
  };
  if (specials.errors.includes(date.getDate())) return maskMissingLayers(values, seed + 19);
  return values;
}

function dayPresentation(day, viewMonth, today, cache) {
  const relation = monthIndex(day) - monthIndex(viewMonth);
  const inMonth = relation === 0;
  const isToday = sameDay(day, today);
  const isAfterToday = startOfDay(day) > today;
  const hasNotHappened = isToday || isAfterToday;

  const base = {
    inMonth,
    today: isToday,
    future: isAfterToday,
    selectable: inMonth && !hasNotHappened,
    beforeToday: startOfDay(day) < today,
  };

  if (hasNotHappened) {
    return {
      ...base,
      opacity: FUTURE_DAY_OPACITY,
      metric: { grid: 100, solar: 100, storage: 100 },
      label: dayCellLabel(day, viewMonth.getMonth()),
    };
  }

  return {
    ...base,
    opacity: inMonth ? 1 : PREVIOUS_MONTH_OPACITY,
    metric: performanceForDate(day, today, cache),
    label: dayCellLabel(day, viewMonth.getMonth()),
  };
}

function formatLayerValue(value) {
  return value == null ? "—" : `${value}%`;
}

const DAY_STEP = {
  ArrowLeft: -1,
  ArrowRight: 1,
};

const WEEK_STEP = {
  ArrowUp: -7,
  ArrowDown: 7,
};

function addDays(date, count) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + count);
}

function happenedBefore(day, today) {
  return Boolean(day) && startOfDay(day) < today;
}

function sameMonth(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function dayInArrowDirection(selectedDate, key, today, bounds) {
  if (!selectedDate) return null;

  if (WEEK_STEP[key]) {
    const nextDay = addDays(selectedDate, WEEK_STEP[key]);
    if (!sameMonth(nextDay, selectedDate) || !happenedBefore(nextDay, today)) return null;
    return { day: nextDay };
  }

  const step = DAY_STEP[key];
  if (!step) return null;

  const nextDay = addDays(selectedDate, step);
  if (!happenedBefore(nextDay, today)) return null;
  if (monthIndex(nextDay) < monthIndex(bounds.earliest)) return null;
  if (monthIndex(nextDay) > monthIndex(bounds.current)) return null;

  return sameMonth(nextDay, selectedDate) ? { day: nextDay } : { day: nextDay, viewMonth: startOfMonth(nextDay) };
}

function isTypingTarget(target) {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
}

function SnapshotDayTooltip({ day, metric }) {
  return (
    <div className="snapshot-day-tooltip">
      <strong>{formatTooltipDay(day)}</strong>
      <div><i className="swatch blue" aria-hidden="true" /> <strong>GRID</strong>: {formatLayerValue(metric.grid)}</div>
      <div><i className="swatch orange" aria-hidden="true" /> <strong>SOLAR</strong>: {formatLayerValue(metric.solar)}</div>
      <div><i className="swatch green" aria-hidden="true" /> <strong>STORAGE</strong>: {formatLayerValue(metric.storage)}</div>
    </div>
  );
}

function SnapshotDay({ state, selected, onSelect }) {
  const { day, label, metric, opacity, inMonth, future, today, selectable, beforeToday } = state;
  const fromPointer = useRef(false);
  const tabStop = selectable && inMonth && day.getDate() === 1;
  const className = [
    "day",
    !inMonth && "outside",
    future && "future",
    today && "today",
    selected && "selected",
  ].filter(Boolean).join(" ");
  const gauge = (
    <>
      <span className="day-number">{label}</span>
      <PerformanceGauge
        grid={metric.grid}
        solar={metric.solar}
        storage={metric.storage}
        opacity={opacity}
      />
    </>
  );

  const cell = selectable ? (
    <div
      className={className}
      role="button"
      tabIndex={tabStop ? 0 : -1}
      aria-pressed={selected}
      aria-label={formatSelectedDay(day)}
      onPointerDown={() => { fromPointer.current = true; }}
      onFocus={() => {
        if (fromPointer.current) {
          fromPointer.current = false;
          return;
        }
        if (tabStop) onSelect(day);
      }}
      onClick={() => onSelect(selected ? null : day)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(selected ? null : day);
        }
      }}
    >
      {gauge}
    </div>
  ) : (
    <div className={className}>{gauge}</div>
  );

  if (!beforeToday) return cell;

  return (
    <Tooltip position="top" tabIndex={-1} open={selected} content={<SnapshotDayTooltip day={day} metric={metric} />}>
      {cell}
    </Tooltip>
  );
}

export default function PerformanceSnapshot() {
  const bounds = useMemo(() => {
    const current = startOfMonth(new Date());
    return {
      current,
      earliest: addMonths(current, -LOOKBACK_MONTHS),
    };
  }, []);
  const [viewMonth, setViewMonth] = useState(bounds.current);
  const [selectedDate, setSelectedDate] = useState(null);
  const daysRef = useRef(null);

  const today = useMemo(() => startOfDay(new Date()), []);
  const days = useMemo(() => buildCalendarDays(viewMonth), [viewMonth]);
  const dayStates = useMemo(() => {
    const cache = new Map();
    return days.map((day) => ({ day, ...dayPresentation(day, viewMonth, today, cache) }));
  }, [days, viewMonth, today]);
  const canGoPrev = monthIndex(viewMonth) > monthIndex(bounds.earliest);
  const canGoNext = monthIndex(viewMonth) < monthIndex(bounds.current);
  const monthLabel = formatMonthYear(viewMonth);
  const selectedState = selectedDate
    ? dayStates.find((state) => sameDay(state.day, selectedDate))
    : null;
  const selectedMetric = selectedState?.metric;

  const showMonth = (nextMonth) => {
    setViewMonth(nextMonth);
    setSelectedDate(null);
  };

  useEffect(() => {
    const days = daysRef.current;
    if (!days) return undefined;
    const resumeHover = () => days.classList.remove("keyboard-nav");
    days.addEventListener("pointermove", resumeHover);
    return () => days.removeEventListener("pointermove", resumeHover);
  }, []);

  useEffect(() => {
    if (!selectedDate) return undefined;

    const clearUnlessAnotherDay = (event) => {
      const cell = event.target instanceof Element
        ? event.target.closest("#performanceSnapshot .day[role='button']")
        : null;
      if (cell) return;
      setSelectedDate(null);
    };

    const onKeyDown = (event) => {
      if (isTypingTarget(event.target)) return;

      if (event.key === "Escape") {
        event.preventDefault();
        setSelectedDate(null);
        return;
      }

      if ((!DAY_STEP[event.key] && !WEEK_STEP[event.key]) || event.altKey || event.ctrlKey || event.metaKey) return;

      const next = dayInArrowDirection(selectedDate, event.key, today, bounds);
      event.preventDefault();
      if (!next) return;
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      daysRef.current?.classList.add("keyboard-nav");
      if (next.viewMonth) setViewMonth(next.viewMonth);
      setSelectedDate(next.day);
    };

    document.addEventListener("pointerdown", clearUnlessAnotherDay);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", clearUnlessAnotherDay);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedDate, today, bounds]);

  return (
    <div id="performanceSnapshot" className="panel p-3 mb-5">
      <div className="header d-flex justify-content-between pb-3 mb-3">
        <div className="left d-flex gap-3 col-8 me-5">
          <span className="d-flex align-items-center gap-2 flex-shrink-0"><FontAwesomeIcon icon={faChartColumn} /> Performance Snapshot Calendar</span>
        </div>
        <div className="right d-flex align-items-stretch gap-0 col">
          <div className="performance-status-legend d-flex align-items-center justify-content-between w-100 flex-direction-row">
            <span><i className="swatch blue" aria-hidden="true" /> Grid </span>
            <span><i className="swatch orange" aria-hidden="true" /> Solar</span>
            <span><i className="swatch green" aria-hidden="true" /> Storage</span>
            <button type="button"><FontAwesomeIcon icon={faGear} /></button>
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center">
        <div className="col-8">
          <div className="calendar">
            <div className="header">
              {canGoPrev ? (
                <button type="button" onClick={() => showMonth(addMonths(viewMonth, -1))}>
                  &laquo; Prev Month
                </button>
              ) : <span aria-hidden="true" />}
              <h4>{monthLabel}</h4>
              {canGoNext ? (
                <button type="button" className="next" onClick={() => showMonth(addMonths(viewMonth, 1))}>
                  Next Month &raquo;
                </button>
              ) : <span aria-hidden="true" />}
            </div>
            <div className="dayLabels">
              {WEEKDAY_LABELS.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
            <div className="days" ref={daysRef}>
              {dayStates.map((state) => (
                <SnapshotDay
                  key={state.day.toDateString()}
                  state={state}
                  selected={state.beforeToday && sameDay(state.day, selectedDate)}
                  onSelect={setSelectedDate}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="col ms-5">
          <div className={selectedDate ? "selected" : "selected d-none"}>
            <h5 className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>Selected Day</strong><span>{selectedDate ? formatSelectedDay(selectedDate) : ""}</span></h5>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>Grid Performance</strong> <span>{selectedMetric ? formatLayerValue(selectedMetric.grid) : ""}</span>
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>Solar Performance</strong> <span>{selectedMetric ? formatLayerValue(selectedMetric.solar) : ""}</span>
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>Storage Performance</strong> <span>{selectedMetric ? formatLayerValue(selectedMetric.storage) : ""}</span>
            </div>
            <hr/>
          </div>
          <div className="current">
            <h5 className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>Stats this Month</strong><span>{monthLabel}</span></h5>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>Days Above Expectations</strong> <span>10 Days</span>
            </div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
              <strong>Days Below Expectations</strong> <span>3 Days</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
